import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api, { PAYMENTS_QA_TIER_LIMIT_PENCE } from "../../../../util/api";
import { getApiErrorMessageWithForbiddenFallback } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

type QaStatus = Awaited<ReturnType<typeof api.getCompanyPaymentsBillingQa>>;
type QaSubscription = QaStatus["subscriptions"][number];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
  font-family: Montserrat, sans-serif;
`;

const WarningBanner = styled.div`
  background: rgba(249, 160, 0, 0.17);
  border: 1px solid #f9a000;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: #13273f;
  line-height: 1.5;
`;

const InfoBanner = styled(WarningBanner)`
  background: rgba(2, 122, 255, 0.08);
  border-color: #027aff;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 16px;
`;

const CardTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #13273f;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
`;

const Select = styled.select`
  min-width: 220px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  padding: 0 10px;
  font-size: 13px;
`;

const Input = styled.input`
  min-width: 180px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  padding: 0 10px;
  font-size: 13px;
`;

const SubCard = styled(Card)`
  margin-top: 8px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  font-size: 12px;
  color: #374151;
`;

const MetaItem = styled.div`
  strong {
    display: block;
    color: #6b7280;
    font-weight: 500;
    margin-bottom: 2px;
  }
`;

const Tag = styled.span<{ $ok?: boolean }>`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) =>
    p.$ok ? "rgba(44, 209, 158, 0.2)" : "rgba(129, 129, 165, 0.2)"};
  color: ${(p) => (p.$ok ? "#00bc82" : "#5c6784")};
`;

const ResultList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #374151;
`;

const formatPence = (p: number) =>
  `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-GB") : "—";

export const CompanyPaymentsBillingQaTab: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const [data, setData] = useState<QaStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [patching, setPatching] = useState<string | null>(null);
  const [txCount, setTxCount] = useState("10");
  const [volumeMinor, setVolumeMinor] = useState("100000");
  const [overLimitTxCount, setOverLimitTxCount] = useState("5");
  const [lastPatchResult, setLastPatchResult] = useState<{
    applied: Array<{ target: string; field: string; value: string | null }>;
    skipped: Array<{ target: string; reason: string }>;
  } | null>(null);
  const [advancedSubscriptionId, setAdvancedSubscriptionId] = useState("");
  const [advancedFields, setAdvancedFields] = useState({
    lastInterimChargeAt: "",
    currentPeriodStart: "",
    currentPeriodEnd: "",
    levelUsed: "",
    unitCount: "",
    unitCountOverTheLimit: "",
    levelUsedAtLastInterimCharge: "",
    unitCountAtLastInterimCharge: "",
    trialExpiresAt: "",
    createdAt: "",
    paymentsInterimBillingInterval: "",
    automatedBillingEnabled: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.getCompanyPaymentsBillingQa(companyId);
      setData(payload);
      if (!advancedSubscriptionId && payload.subscriptions[0]) {
        setAdvancedSubscriptionId(payload.subscriptions[0].subscriptionId);
      }
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(
          e,
          "Failed to load payments billing QA status"
        ),
        NotificationTypes.DANGER
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const runPreset = async (
    key: string,
    body: Parameters<typeof api.patchCompanyPaymentsBillingQa>[1]
  ) => {
    setPatching(key);
    try {
      const result = await api.patchCompanyPaymentsBillingQa(companyId, body);
      setLastPatchResult(result);
      showNotification("QA preset applied", NotificationTypes.SUCCESS);
      await load();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(e, "Preset failed"),
        NotificationTypes.DANGER
      );
    } finally {
      setPatching(null);
    }
  };

  const saveAdvanced = async () => {
    const fields: Record<string, string | number | boolean | null> = {};
    if (advancedFields.lastInterimChargeAt) {
      fields.lastInterimChargeAt = new Date(
        advancedFields.lastInterimChargeAt
      ).toISOString();
    }
    if (advancedFields.currentPeriodStart) {
      fields.currentPeriodStart = new Date(
        advancedFields.currentPeriodStart
      ).toISOString();
    }
    if (advancedFields.currentPeriodEnd) {
      fields.currentPeriodEnd = new Date(
        advancedFields.currentPeriodEnd
      ).toISOString();
    }
    if (advancedFields.trialExpiresAt) {
      fields.trialExpiresAt = new Date(
        advancedFields.trialExpiresAt
      ).toISOString();
    }
    if (advancedFields.createdAt) {
      fields.createdAt = new Date(advancedFields.createdAt).toISOString();
    }
    if (advancedFields.levelUsed) {
      fields.levelUsed = Number(advancedFields.levelUsed);
    }
    if (advancedFields.unitCount) {
      fields.unitCount = Number(advancedFields.unitCount);
    }
    if (advancedFields.unitCountOverTheLimit) {
      fields.unitCountOverTheLimit = Number(
        advancedFields.unitCountOverTheLimit
      );
    }
    if (advancedFields.levelUsedAtLastInterimCharge) {
      fields.levelUsedAtLastInterimCharge = Number(
        advancedFields.levelUsedAtLastInterimCharge
      );
    }
    if (advancedFields.unitCountAtLastInterimCharge) {
      fields.unitCountAtLastInterimCharge = Number(
        advancedFields.unitCountAtLastInterimCharge
      );
    }
    if (advancedFields.paymentsInterimBillingInterval) {
      fields.paymentsInterimBillingInterval =
        advancedFields.paymentsInterimBillingInterval === "null"
          ? null
          : advancedFields.paymentsInterimBillingInterval;
    }
    if (advancedFields.automatedBillingEnabled) {
      fields.automatedBillingEnabled =
        advancedFields.automatedBillingEnabled === "true";
    }

    await runPreset("advanced", {
      mode: "patch",
      subscriptionId: advancedSubscriptionId || undefined,
      fields,
    });
  };

  const renderSubscription = (sub: QaSubscription) => (
    <SubCard key={sub.subscriptionId}>
      <CardTitle>
        {sub.subscriptionOption}
        {sub.subscriptionLevel ? ` · ${sub.subscriptionLevel}` : ""} ·{" "}
        {sub.status} · {sub.subscriptionId.slice(-6)}
      </CardTitle>
      <MetaGrid>
        <MetaItem>
          <strong>Interim due</strong>
          <Tag $ok={sub.computed.interimDueYet}>
            {sub.computed.interimDueYet ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>EOP due</strong>
          <Tag $ok={sub.computed.eopDueYet}>
            {sub.computed.eopDueYet ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>Period expired</strong>
          <Tag $ok={sub.computed.periodExpired}>
            {sub.computed.periodExpired ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>Already invoiced this period</strong>
          <Tag $ok={!sub.computed.alreadyInvoicedThisPeriod}>
            {sub.computed.alreadyInvoicedThisPeriod ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>Closing invoice</strong>
          {sub.closingInvoice
            ? `${
                sub.closingInvoice.invoiceNumber ??
                sub.closingInvoice.invoiceId.slice(-6)
              } · ${sub.closingInvoice.status ?? "?"} · ${formatPence(
                sub.closingInvoice.totalPence
              )}`
            : "—"}
        </MetaItem>
        <MetaItem>
          <strong>Interim invoices this period</strong>
          {sub.interimInvoicesThisPeriod.count} ·{" "}
          {formatPence(sub.interimInvoicesThisPeriod.creditsPence)} credits
        </MetaItem>
        <MetaItem>
          <strong>Volume used</strong>
          {formatPence(sub.counters.levelUsed)}
        </MetaItem>
        <MetaItem>
          <strong>Tx count</strong>
          {sub.counters.unitCount}
        </MetaItem>
        <MetaItem>
          <strong>Tx over limit</strong>
          {sub.counters.unitCountOverTheLimit}
        </MetaItem>
        <MetaItem>
          <strong>Period start</strong>
          {formatDate(sub.timing.currentPeriodStart)}
        </MetaItem>
        <MetaItem>
          <strong>Period end</strong>
          {formatDate(sub.timing.currentPeriodEnd)}
        </MetaItem>
        <MetaItem>
          <strong>Last interim charge</strong>
          {formatDate(sub.timing.lastInterimChargeAt)}
        </MetaItem>
        <MetaItem>
          <strong>EOP invoice id</strong>
          {sub.timing.lastEndOfPeriodInvoiceId ?? "—"}
        </MetaItem>
        <MetaItem>
          <strong>EOP processing result</strong>
          {sub.timing.lastEndOfPeriodProcessingResult ?? "—"}
        </MetaItem>
        <MetaItem>
          <strong>EOP processed at</strong>
          {formatDate(sub.timing.lastEndOfPeriodProcessedAt)}
        </MetaItem>
        <MetaItem>
          <strong>Subscription created at</strong>
          {formatDate(sub.timing.createdAt)}
        </MetaItem>
        <MetaItem>
          <strong>Trial expires at</strong>
          {formatDate(sub.timing.trialExpiresAt)}
        </MetaItem>
        {sub.computed.prorate ? (
          <>
            <MetaItem>
              <strong>Prorate paid start (cron)</strong>
              {formatDate(sub.computed.prorate.effectivePaidStart)}
            </MetaItem>
            <MetaItem>
              <strong>Partial period ratio</strong>
              {(sub.computed.prorate.partialPeriodRatio * 100).toFixed(2)}%
              {sub.computed.prorate.billableDays != null &&
              sub.computed.prorate.totalDays != null
                ? ` (${sub.computed.prorate.billableDays}/${sub.computed.prorate.totalDays} UTC days)`
                : ""}
            </MetaItem>
          </>
        ) : null}
      </MetaGrid>
      <Row style={{ marginTop: 12 }}>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={patching !== null}
          onClick={() =>
            void runPreset(`interim-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "make_interim_due",
              subscriptionId: sub.subscriptionId,
            })
          }
        >
          {patching === `interim-${sub.subscriptionId}`
            ? "Applying…"
            : "Make interim due"}
        </Button>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={patching !== null}
          onClick={() =>
            void runPreset(`eop-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "make_eop_due",
              subscriptionId: sub.subscriptionId,
            })
          }
        >
          {patching === `eop-${sub.subscriptionId}`
            ? "Applying…"
            : "Make EOP due"}
        </Button>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={patching !== null}
          onClick={() =>
            void runPreset(`reset-eop-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "reset_eop_retest",
              subscriptionId: sub.subscriptionId,
            })
          }
        >
          {patching === `reset-eop-${sub.subscriptionId}`
            ? "Applying…"
            : "Reset EOP retest"}
        </Button>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={patching !== null}
          onClick={() =>
            void runPreset(`clear-interim-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "clear_interim_charge",
              subscriptionId: sub.subscriptionId,
            })
          }
        >
          {patching === `clear-interim-${sub.subscriptionId}`
            ? "Applying…"
            : "Clear interim charge"}
        </Button>
      </Row>
      <Row style={{ marginTop: 12 }}>
        <Field>
          Tx count
          <Input
            type="number"
            min={0}
            value={txCount}
            onChange={(e) => setTxCount(e.target.value)}
          />
        </Field>
        <Field>
          Volume (pence)
          <Input
            type="number"
            min={0}
            value={volumeMinor}
            onChange={(e) => setVolumeMinor(e.target.value)}
          />
        </Field>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={patching !== null}
          onClick={() =>
            void runPreset(`usage-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "add_usage",
              subscriptionId: sub.subscriptionId,
              params: {
                unitCount: Number(txCount) || 0,
                volumeMinor: Number(volumeMinor) || 0,
              },
            })
          }
        >
          {patching === `usage-${sub.subscriptionId}`
            ? "Applying…"
            : "Add usage"}
        </Button>
      </Row>
      <Row style={{ marginTop: 12 }}>
        <Field>
          Over-limit tx count
          <Input
            type="number"
            min={1}
            value={overLimitTxCount}
            onChange={(e) => setOverLimitTxCount(e.target.value)}
            disabled={sub.counters.levelUsed < PAYMENTS_QA_TIER_LIMIT_PENCE}
          />
        </Field>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          disabled={
            patching !== null ||
            sub.counters.levelUsed < PAYMENTS_QA_TIER_LIMIT_PENCE ||
            !(Number(overLimitTxCount) > 0)
          }
          title={
            sub.counters.levelUsed < PAYMENTS_QA_TIER_LIMIT_PENCE
              ? "Requires volume used >= £1M (100,000,000 pence). Use Add usage first."
              : undefined
          }
          onClick={() =>
            void runPreset(`over-limit-${sub.subscriptionId}`, {
              mode: "preset",
              preset: "add_over_limit",
              subscriptionId: sub.subscriptionId,
              params: {
                overLimitTxCount: Number(overLimitTxCount) || 1,
              },
            })
          }
        >
          {patching === `over-limit-${sub.subscriptionId}`
            ? "Applying…"
            : "Add over-limit txs"}
        </Button>
      </Row>
      {sub.counters.levelUsed < PAYMENTS_QA_TIER_LIMIT_PENCE ? (
        <Typography variant={TypographyVariant.BODY2} style={{ marginTop: 4 }}>
          Over-limit counter unlocks when volume used reaches £1M. Invoice
          over-limit line also needs volume above the plan&apos;s top tier.
        </Typography>
      ) : null}
    </SubCard>
  );

  if (loading && !data) {
    return (
      <Wrap>
        <Typography variant={TypographyVariant.BODY1}>Loading…</Typography>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <WarningBanner>
        <strong>QA only (non-production).</strong> Presets backdate timing /
        usage fields so the payments billing cron can be tested after a manual
        GCP force-run. Cron processes <strong>all eligible merchants</strong> on
        the environment — prefer an isolated sandbox company with a GoCardless
        sandbox mandate. If the company flag <code>isTestCompany</code> is set,
        the cron still skips provider charges (<code>TEST_COMPANY_SKIP</code>) —
        leave it unset to create real sandbox DD payments.
      </WarningBanner>

      <Card>
        <CardTitle>Company billing flags</CardTitle>
        <MetaGrid>
          <MetaItem>
            <strong>Automated billing</strong>
            <Tag $ok={!!data?.company.automatedBillingEnabled}>
              {data?.company.automatedBillingEnabled ? "Enabled" : "Disabled"}
            </Tag>
          </MetaItem>
          <MetaItem>
            <strong>Interim interval</strong>
            {data?.company.paymentsInterimBillingInterval ?? "— (EOP only)"}
          </MetaItem>
        </MetaGrid>
      </Card>

      <Row>
        <Button
          type="button"
          variant={ButtonVariant.CONTAINED}
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
        <Link
          to={`/private/subscription/upcoming-charges`}
          style={{ fontSize: 13, alignSelf: "center" }}
        >
          Upcoming charges
        </Link>
        <Link
          to={`/private/subscription/billing-test`}
          style={{ fontSize: 13, alignSelf: "center" }}
        >
          Billing test (in-process)
        </Link>
      </Row>

      <Card>
        <CardTitle>Interim · EOP → payments billing cron</CardTitle>
        <Typography variant={TypographyVariant.BODY2}>
          After presets, force-run{" "}
          <code>{data?.gcpCronRunbook.paymentsBilling.service}</code> (
          {data?.gcpCronRunbook.paymentsBilling.schedulerJob}) in GCP Cloud
          Scheduler. Phases: GC sync → trials → end-of-period → interim → retry
          → one-shot packs.
        </Typography>
        <Typography variant={TypographyVariant.BODY2} style={{ marginTop: 8 }}>
          <strong>Make interim due</strong> requires{" "}
          <code>paymentsInterimBillingInterval</code> and an{" "}
          <strong>active</strong> PAYMENTS_ONLY subscription.{" "}
          <strong>Make EOP due</strong> sets <code>currentPeriodStart</code> /{" "}
          <code>currentPeriodEnd</code> to the last fully closed billing period
          (e.g. all of June when the sub was on July) and clears EOP stamps.
          Cron skips EOP if a closing <code>BillingInvoice</code> already exists
          for that period — use <strong>Reset EOP retest</strong> to soft-delete
          closing + interim invoices and clear EOP/interim stamps. Enable{" "}
          <code>automatedBillingEnabled</code> if you want an invoice (opted-out
          companies only roll the period forward).
        </Typography>
      </Card>

      {(data?.subscriptions ?? []).length === 0 && (
        <InfoBanner>
          No active/trialing subscriptions found for this company (excluding
          Shopify B2B).
        </InfoBanner>
      )}

      {(data?.subscriptions ?? []).map(renderSubscription)}

      <Card>
        <CardTitle>Advanced manual fields</CardTitle>
        <Typography
          variant={TypographyVariant.BODY2}
          style={{ marginBottom: 12 }}
        >
          Monthly PAYMENTS_ONLY prorate: cron uses{" "}
          <code>max(periodStart, createdAt, trialExpiresAt)</code>. After
          editing dates, refresh and check <strong>Partial period ratio</strong>{" "}
          on the subscription card, then run <strong>Make EOP due</strong> and
          force-run the cron on a closed period.
        </Typography>
        <Row>
          <Field>
            Subscription
            <Select
              value={advancedSubscriptionId}
              onChange={(e) => setAdvancedSubscriptionId(e.target.value)}
            >
              {(data?.subscriptions ?? []).map((s) => (
                <option key={s.subscriptionId} value={s.subscriptionId}>
                  {s.subscriptionOption} ({s.subscriptionId.slice(-8)})
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Interim interval (company)
            <Select
              value={advancedFields.paymentsInterimBillingInterval}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  paymentsInterimBillingInterval: e.target.value,
                }))
              }
            >
              <option value="">— leave unchanged —</option>
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
              <option value="biweekly">biweekly</option>
              <option value="null">clear (null)</option>
            </Select>
          </Field>
          <Field>
            Automated billing (company)
            <Select
              value={advancedFields.automatedBillingEnabled}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  automatedBillingEnabled: e.target.value,
                }))
              }
            >
              <option value="">— leave unchanged —</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </Select>
          </Field>
          <Field>
            Last interim charge (local)
            <Input
              type="datetime-local"
              value={advancedFields.lastInterimChargeAt}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  lastInterimChargeAt: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Period start (local)
            <Input
              type="datetime-local"
              value={advancedFields.currentPeriodStart}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  currentPeriodStart: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Period end (local)
            <Input
              type="datetime-local"
              value={advancedFields.currentPeriodEnd}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  currentPeriodEnd: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Trial expires at (local)
            <Input
              type="datetime-local"
              value={advancedFields.trialExpiresAt}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  trialExpiresAt: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Subscription created at (local)
            <Input
              type="datetime-local"
              value={advancedFields.createdAt}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  createdAt: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Level used (pence)
            <Input
              type="number"
              value={advancedFields.levelUsed}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  levelUsed: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Transaction count
            <Input
              type="number"
              value={advancedFields.unitCount}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  unitCount: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Tx over limit (absolute)
            <Input
              type="number"
              min={0}
              value={advancedFields.unitCountOverTheLimit}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  unitCountOverTheLimit: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Level at last interim
            <Input
              type="number"
              value={advancedFields.levelUsedAtLastInterimCharge}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  levelUsedAtLastInterimCharge: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Tx at last interim
            <Input
              type="number"
              value={advancedFields.unitCountAtLastInterimCharge}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  unitCountAtLastInterimCharge: e.target.value,
                }))
              }
            />
          </Field>
        </Row>
        <Row style={{ marginTop: 12 }}>
          <Button
            type="button"
            variant={ButtonVariant.CONTAINED}
            disabled={patching !== null}
            onClick={() => void saveAdvanced()}
          >
            Save advanced fields
          </Button>
        </Row>
      </Card>

      {lastPatchResult && (
        <Card>
          <CardTitle>Last patch result</CardTitle>
          {lastPatchResult.applied.length > 0 && (
            <>
              <strong>Applied</strong>
              <ResultList>
                {lastPatchResult.applied.map((a, i) => (
                  <li key={i}>
                    {a.target}: {a.field} = {a.value ?? "(cleared)"}
                  </li>
                ))}
              </ResultList>
            </>
          )}
          {lastPatchResult.skipped.length > 0 && (
            <>
              <strong>Skipped</strong>
              <ResultList>
                {lastPatchResult.skipped.map((s, i) => (
                  <li key={i}>
                    {s.target}: {s.reason}
                  </li>
                ))}
              </ResultList>
            </>
          )}
        </Card>
      )}
    </Wrap>
  );
};

export default CompanyPaymentsBillingQaTab;
